import React from 'react';
import { gql, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { ERROR_MESSAGE, LOADING_MESSAGE } from '../../components/Messages/Messages';
import HeadingBannerTitle from './components/HeadingBanner/HeadingBannerTitle';
import IconBlockDisplay from './components/IconBlock/IconBlockDisplay';
import StoreAddress from './components/StoreAddress/StoreAddress';
import StoreDirection from './components/StoreDirection/StoreDirection';
import StoreHours from './components/StoreHours/StoreHours';
import StoreMessage from './components/StoreMessage/StoreMessage';
import StoreShoppingCategories from './components/StoreShoppingCategories/StoreShoppingCategories';
import MapWrapper from './MapWrapper';

const GET_SINGLE_STORE = gql`
  query GetSingleStore($storeNumber: String!) {
    textFor {
      viewAllStoresText: pair(pairKey : "viewAllStoresText") {...textPair}
      getDirectionsText: pair(pairKey: "getDirectionsText") {...textPair}
      globalStoreHours: pair(pairKey: "globalStoreHours") {...textPair}
      globalOpeningSoon: pair(pairKey: "globalOpeningSoon") {...textPair}
      shopThisStoreOnlineText: pair(pairKey: "shopThisStoreOnlineText") {...textPair}
      storeText: pair(pairKey: "storeText") {...textPair}
    }
    singleStore(storeNumber: $storeNumber) {
      storeName
      city
      addressLine
      stateOrProvinceName
      country
      postalCode
      countryCode
      telephone
      viewAllStoresUrl
      mapUrl
      onlineStoreId
      latitude
      longitude
      storeHours {
        day
        month
        isClosed
        open12HourFormat
        close12HourFormat
        weekdayName { ...textPair }
      }
      additionalStoreMessaging {...textPair }
      firstShopLinkText {...textPair}
      firstShopLinkUrl
      secondShopLinkText {...textPair}
      secondShopLinkUrl
      brandText {...textPair}
      isOpeningSoon
      isOutlet
      storeApiMessaging {...textPair}
      hasShopYourLocalStore
      isStoreIdEqualToOnlineStoreId
      brandFromAPI
      isCity
      isState
      isPostalCode
      isCountry
      isPhone
      isPhysicalStore
      paymentsAcceptedSpecificCountry
      googleMapApiKey
    }
  }
  fragment textPair on TextPair {
    key
    value
  }
`;

export default function SingleStorePage({ storeNumber }) {
  const { loading, error, data } = useQuery(GET_SINGLE_STORE, {
    variables: { storeNumber },
  });
  if (loading) return LOADING_MESSAGE;
  if (error) return ERROR_MESSAGE;

  const {
    latitude, longitude, brandText, viewAllStoresUrl, storeName,
    addressLine, city, stateOrProvinceName, postalCode, countryCode,
    country, telephone, isCity, isState, isPostalCode, isCountry, isPhone,
    paymentsAcceptedSpecificCountry, mapUrl, storeApiMessaging, isOpeningSoon,
    storeHours, additionalStoreMessaging, hasShopYourLocalStore,
    isOutlet, firstShopLinkText, firstShopLinkUrl, secondShopLinkText, secondShopLinkUrl,
    isStoreIdEqualToOnlineStoreId, brandFromAPI, isPhysicalStore,
    googleMapApiKey,
  } = data.singleStore;
  const {
    storeText, viewAllStoresText, getDirectionsText,
    globalStoreHours, globalOpeningSoon, shopThisStoreOnlineText,
  } = data.textFor;

  return (
    <div className="mainWrapper">
      <div className="single-store-wrapper">
        <div id="single-store-page" className="map-with-panel">
          <div id="single-store-page__panel" className="map-with-panel__panel">
            <HeadingBannerTitle
              brandText={brandText}
              viewAllStoresUrl={viewAllStoresUrl}
              storeText={storeText}
              viewAllStoresText={viewAllStoresText}
            />
            <div className="single-store__store-location">
              <section className="single-store__info">
                <div>
                  <IconBlockDisplay storeName={storeName} />
                  <div className="single-store__info-space">
                    <StoreAddress
                      addressLine={addressLine}
                      city={city}
                      stateOrProvinceName={stateOrProvinceName}
                      postalCode={postalCode}
                      countryCode={countryCode}
                      country={country}
                      telephone={telephone}
                      isCity={isCity}
                      isState={isState}
                      isPostalCode={isPostalCode}
                      isCountry={isCountry}
                      isPhone={isPhone}
                      paymentsAcceptedSpecificCountry={paymentsAcceptedSpecificCountry}
                    />
                    <StoreDirection mapUrl={mapUrl} getDirectionsText={getDirectionsText} />
                    <StoreMessage storeApiMessaging={storeApiMessaging} />
                    <StoreHours
                      isOpeningSoon={isOpeningSoon}
                      storeHours={storeHours}
                      additionalStoreMessaging={additionalStoreMessaging}
                      globalStoreHours={globalStoreHours}
                      globalOpeningSoon={globalOpeningSoon}
                    />
                  </div>
                </div>
                <StoreShoppingCategories
                  hasShopYourLocalStore={hasShopYourLocalStore}
                  isOutlet={isOutlet}
                  firstShopLinkText={firstShopLinkText}
                  firstShopLinkUrl={firstShopLinkUrl}
                  secondShopLinkText={secondShopLinkText}
                  secondShopLinkUrl={secondShopLinkUrl}
                  isStoreIdEqualToOnlineStoreId={isStoreIdEqualToOnlineStoreId}
                  brandFromAPI={brandFromAPI}
                  shopThisStoreOnlineText={shopThisStoreOnlineText}
                />
              </section>
            </div>
          </div>
        </div>
        <MapWrapper
          isPhysicalStore={isPhysicalStore}
          latitude={latitude}
          longitude={longitude}
          googleMapApiKey={googleMapApiKey}
        />
      </div>
    </div>
  );
}

SingleStorePage.propTypes = {
  storeNumber: PropTypes.string.isRequired,
};
